import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Paper,
  Fade,
  Grid,
  Stack,
  Avatar,
  Card,
  CardContent,
  Divider,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Schedule,
  Business,
  Receipt,
  CalendarToday,
  Warning as WarningIcon,
  CheckCircle,
  Cancel,
  Description,
  ExpandMore,
  ExpandLess,
  Download,
} from '@mui/icons-material';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import { creatorTheme } from './themes';
import ApprovalStamp from './approvalstamp';
import InvoiceActions from './invoiceactions';
import BankingInfo from './bankinginfo';
import PaymentCTA from './paymentCTA';

const CreatorInvoiceDesign = ({ 
  invoice,
  companyInfo,
  invoiceSettings,
  onDownloadPDF,
  onCopyInvoiceNumber, 
  onShareInvoice,
  pdfGenerating, 
  formatDate, 
  getStatusColor, 
  getStatusText,
  getEffectiveValue,
  getInvoiceNotes,
  getInvoiceTerms,
  daysLate,
  totalDaysToPay,
  calculatedDueDate,
  supportingDocument,
  onOpenPdfViewer,
  needsApproval,
  userInfo,
  onOpenApprovalDialog,
  onOpenLoginDialog,
  billedToEmail,
  billedToName,
  isAuthenticated,
}) => {
  const [showSupportingDoc, setShowSupportingDoc] = useState(false);
  
  // Use getEffectiveValue for all customizable fields - matching RegularInvoiceDesign pattern
  const effectiveInvoiceNumber = getEffectiveValue('invoice_number', invoice?.po_number);
  const effectiveDateIssued = getEffectiveValue('date_of_issue', invoice?.created_at);
  const effectiveBilledTo = getEffectiveValue('billed_to', invoice?.client_name || billedToName);
  
  // Invoice data with safe defaults
  const invoiceAmount = invoice?.amount_due || invoice?.amount || 0;
  const invoiceStatus = invoice?.status || invoice?.invoice_status;
  const paymentDate = invoice?.payment_date;
  const paymentTerm = invoice?.payment_term;
  const rawInvoiceItems = Array.isArray(invoice?.invoice_items) ? invoice.invoice_items : [];

  const lineItems = rawInvoiceItems.length
    ? rawInvoiceItems.map((item, index) => {
        const quantity = Number(item?.quantity ?? item?.qty ?? 1) || 1;
        const rate = Number(item?.rate ?? item?.price ?? item?.amount ?? 0) || 0;
        const amount = Number(item?.amount ?? item?.line_total ?? rate * quantity) || 0;
        return {
          id: item?.id || `${effectiveInvoiceNumber}-${index}`,
          description: item?.description || item?.name || `Creative Services - ${effectiveInvoiceNumber}`,
          quantity,
          rate,
          amount,
        };
      })
    : [{
        id: effectiveInvoiceNumber,
        description: `Creative Services - ${effectiveInvoiceNumber}`,
        quantity: 1,
        rate: invoiceAmount,
        amount: invoiceAmount,
      }];
  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const totalAmount = Number(invoice?.amount_due ?? invoice?.amount ?? subtotal) || 0;
  
  // Get customized notes and terms from invoiceSettings
  const invoiceNotes = getInvoiceNotes();
  const invoiceTerms = getInvoiceTerms();
  return (
      <ThemeProvider theme={creatorTheme}>
        <Box
          sx={{
            minHeight: '100vh',
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            color: 'text.primary',
          }}
        >
        <HomeHeader />
        <Toolbar />

        <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
          {/* Action Bar */}
          <InvoiceActions
            onDownloadPDF={onDownloadPDF}
            pdfGenerating={pdfGenerating}
            onCopyInvoiceNumber={onCopyInvoiceNumber}
            onShareInvoice={onShareInvoice}
            invoiceNumber={effectiveInvoiceNumber}
            invoiceStatus={invoiceStatus}
            createdDate={effectiveDateIssued}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
            formatDate={formatDate}
            variant="creator"
            isAuthenticated={isAuthenticated}
          />

          {/* Enhanced Approval Banner with Approver Details */}
          {needsApproval && (
            <Fade in={true} timeout={600}>
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 2, 
                  borderRadius: 3,
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={600}>
                    This invoice requires approval
                  </Typography>
                  
                  <Typography variant="body2">
                    <strong>Action Required:</strong> This invoice needs to be reviewed and approved by the billed party:
                  </Typography>
                  
                  {/* Highlighted Approver Details Box */}
                  <Box sx={{ 
                    pl: 2, 
                    py: 1.5, 
                    borderLeft: '3px solid',
                    borderColor: 'warning.main',
                    bgcolor: 'rgba(237, 108, 2, 0.08)',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Approver Details:
                    </Typography>
                    <Typography variant="body2">
                      Name: <strong>{billedToName || 'Not specified'}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Email: <strong>{billedToEmail || 'Not specified'}</strong>
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    If you are {billedToName || 'the billed party'}, please review the invoice details below and approve or decline.
                  </Typography>
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {userInfo ? (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={onOpenApprovalDialog}
                          sx={{ borderRadius: 2 }}
                        >
                          Approve Invoice
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={onOpenApprovalDialog}
                          sx={{ borderRadius: 2 }}
                        >
                          Decline Invoice
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onOpenLoginDialog}
                        sx={{ borderRadius: 2 }}
                      >
                        Login to Approve/Decline
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Alert>
            </Fade>
          )}

          {/* Invoice Content */}
          <Fade in={true} timeout={800}>
            <Paper
              id="invoice-content"
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                '& *': {
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact',
                }
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  p: 3,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact',
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={2.5} alignItems="center">
                      {companyInfo.logo ? (
                        <Avatar
                          src={companyInfo.logo}
                          sx={{
                            width: 72,
                            height: 72,
                            border: '3px solid #f3f4f6',
                            boxShadow: 3,
                          }}
                          imgProps={{ crossOrigin: 'anonymous' }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 72,
                            height: 72,
                            border: '3px solid #f3f4f6',
                            boxShadow: 3,
                            bgcolor: '#f9fafb',
                            color: 'primary.main',
                            fontSize: '2rem',
                            fontWeight: 700,
                          }}
                        >
                          {companyInfo.name.charAt(0)}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="h4" fontWeight={800} gutterBottom>
                          INVOICE
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          From: {invoice?.creator_info?.creator_id || 'Creator'}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                          {companyInfo.name}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card
                      elevation={0}
                      sx={{
                        background: '#f8fafc',
                        borderRadius: 3,
                        border: '1px solid #d1d5db',
                        maxWidth: 280,
                        ml: { xs: 0, md: 'auto' },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack spacing={1.25} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                          <Typography
                            variant="body2"
                            sx={{ color: '#6b7280', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                          >
                            Total Amount
                          </Typography>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              color: '#111827',
                              lineHeight: 1,
                            }}
                          >
                            ${totalAmount.toFixed(2)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Approval Stamp */}
                {invoice?.approval_proof && (
                  <ApprovalStamp 
                    approvalInfo={invoice.approval_proof}
                    invoice={invoice}
                  />
                )}

                {/* Supporting Document Section */}
                {supportingDocument && (
                  <Card 
                    variant="outlined"
                    data-supporting-doc-section
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'primary.main'
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setShowSupportingDoc(!showSupportingDoc)}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <Description />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              Supporting Documents Attached
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Click to {showSupportingDoc ? 'hide' : 'view'} attached documents
                            </Typography>
                          </Box>
                        </Stack>
                        <IconButton>
                          {showSupportingDoc ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Stack>
                      
                      <Collapse in={showSupportingDoc}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <iframe
                            src={supportingDocument}
                            style={{ 
                              width: '100%', 
                              height: '500px', 
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px'
                            }}
                            title="Supporting Document PDF"
                          />
                          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button
                              variant="outlined"
                              startIcon={<Download />}
                              href={supportingDocument}
                              download
                              target="_blank"
                            >
                              Download Document
                            </Button>
                          </Stack>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Terms, Billing Info, and Invoice Details */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Schedule sx={{ fontSize: 20 }} />
                          Payment Terms
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Payment Term:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {paymentTerm ? `${paymentTerm} days` : 'N/A'}
                            </Typography>
                          </Stack>
                          {paymentDate && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2">Payment Date:</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {formatDate(paymentDate)}
                              </Typography>
                            </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Total Days to Pay:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {totalDaysToPay !== 'N/A' ? `${totalDaysToPay} days` : 'N/A'}
                            </Typography>
                          </Stack>
                          {daysLate !== 'N/A' && daysLate > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="error">Days Late:</Typography>
                              <Typography variant="body2" fontWeight={500} color="error">
                                {daysLate} days
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Business sx={{ fontSize: 20 }} />
                          Billed To
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body1" fontWeight={500}>
                            {effectiveBilledTo || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoice?.client_email || billedToEmail || 'N/A'}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Receipt sx={{ fontSize: 20 }} />
                          Invoice Details
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Invoice #:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {effectiveInvoiceNumber}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Date Issued:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {formatDate(effectiveDateIssued)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Due Date:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {calculatedDueDate ? formatDate(calculatedDueDate) : 'N/A'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Status:</Typography>
                            <Chip 
                              label={getStatusText(invoiceStatus)}
                              color={getStatusColor(invoiceStatus)}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Services Table */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                    Services
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Table>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ py: 2 }}><strong>Description</strong></TableCell>
                          <TableCell align="center" sx={{ py: 2 }}><strong>Quantity</strong></TableCell>
                          <TableCell align="right" sx={{ py: 2 }}><strong>Rate</strong></TableCell>
                          <TableCell align="right" sx={{ py: 2 }}><strong>Amount</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lineItems.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body1">
                                {item.description}
                              </Typography>
                              {index === 0 && invoiceNotes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                  {invoiceNotes}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>{item.quantity}</TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>${item.rate.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="body1" fontWeight={600}>
                                ${item.amount.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                    <Box sx={{ width: '100%', maxWidth: 320 }}>
                      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body1" color="text.secondary">Subtotal</Typography>
                              <Typography variant="body1">${subtotal.toFixed(2)}</Typography>
                            </Stack>

                            <Divider />

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="h6" fontWeight={700}>Total</Typography>
                              <Typography variant="h6" fontWeight={700} color="text.primary">
                                ${totalAmount.toFixed(2)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                </Box>

                {/* Banking Information - using companyInfo from invoiceSettings */}
                <BankingInfo companyInfo={companyInfo} variant="creator" />

                {/* Payment Terms & Conditions - using invoiceSettings customization */}
                {invoiceTerms && (
                  <Box sx={{ mt: 3 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Payment Terms & Conditions
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {invoiceTerms}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                )}

                {/* Payment CTA */}
                <PaymentCTA 
                  invoice={invoice} 
                  invoiceAmount={invoiceAmount} 
                  variant="creator" 
                />

                {/* Footer - using companyInfo from invoiceSettings */}
                <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Questions about this invoice? Contact us at {companyInfo.email || 'win@useblitz.co'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mt: 0.5 }}>
                    {companyInfo.name}
                  </Typography>
                  {companyInfo.address_line_1 && (
                    <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                      {companyInfo.address_line_1}
                      {companyInfo.city && `, ${companyInfo.city}`}
                      {companyInfo.state && `, ${companyInfo.state}`}
                      {companyInfo.postal_code && ` ${companyInfo.postal_code}`}
                    </Typography>
                  )}
                  {companyInfo.phone_number && (
                    <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                      Phone: {companyInfo.phone_number}
                    </Typography>
                  )}
                  {companyInfo.website && (
                    <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                      {companyInfo.website}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Container>

        <HomeFooter />
      </Box>
    </ThemeProvider>
  );
};

export default CreatorInvoiceDesign;
